// v642.5: Single source of truth for character display data.
//
// Wraps the existing gameState.character so any component can pull the
// canonical level / gold / crowns / HP / MP / XP / equipment via a hook
// instead of prop-drilling. Eliminates the class of bug where a new
// display site reads stale data because someone forgot to wire a refresh.
//
// Design notes:
//   - The provider is dumb: it just memoizes a derived view of whatever
//     `character` was passed in. The owning component (GamePage) still
//     owns the underlying state.
//   - We DON'T introduce a new piece of state. There's only ever one
//     copy of the character, owned by GamePage.
//   - Existing components that take a `character` prop continue to work
//     unchanged. Only new code (and the GamePage HUD strips) uses the
//     hook.

import React, { createContext, useContext, useMemo } from 'react';

const CharacterContext = createContext(null);

const safeNumber = (n, fallback = 0) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
};

export const CharacterProvider = ({ character, children }) => {
  // Memoize the derived shape so consumers don't re-render unless the
  // underlying numbers change. character itself is identity-stable
  // unless GamePage's setGameState replaces it.
  const value = useMemo(() => {
    if (!character) {
      return {
        character: null,
        gold: 0,
        crowns: 0,
        level: 1,
        hp: 0,
        hpMax: 1,
        mp: 0,
        mpMax: 1,
        experience: 0,
        worldTier: 0,
        equipment: {},
      };
    }
    return {
      character,
      gold: safeNumber(character.gold, 0),
      crowns: safeNumber(character.crowns, 0),
      level: safeNumber(character.level, 1),
      hp: safeNumber(character.hp, 0),
      hpMax: safeNumber(character.hp_max, 1),
      mp: safeNumber(character.mp, 0),
      mpMax: safeNumber(character.mp_max, 1),
      experience: safeNumber(character.experience, 0),
      worldTier: safeNumber(character.world_tier, 0),
      equipment: character.equipment || {},
    };
  }, [character]);

  return (
    <CharacterContext.Provider value={value}>
      {children}
    </CharacterContext.Provider>
  );
};

/**
 * useCharacter — pull canonical character display data.
 * Returns null-safe defaults when no character is loaded.
 */
export const useCharacter = () => {
  const ctx = useContext(CharacterContext);
  if (!ctx) {
    // Hook was called outside a provider — return harmless defaults so
    // a component can still render (avoids hard crash if mounted early).
    return {
      character: null,
      gold: 0, crowns: 0, level: 1,
      hp: 0, hpMax: 1, mp: 0, mpMax: 1,
      experience: 0, worldTier: 0, equipment: {},
    };
  }
  return ctx;
};

export default CharacterContext;
